/**
 * ScrollButton - A web component that enables scrolling a target element horizontally or vertically.
 * 
 * Attributes:
 * - direction: The direction to scroll ('left', 'right', 'up', 'down')
 * - target: ID of the element to scroll
 * - amount: (optional) The amount to scroll in pixels (default: 300)
 * - behavior: (optional) The scroll behavior ('smooth', 'auto', 'instant') (default: 'smooth')
 * 
 * Usage:
 * <scroll-button direction="left" target="my-element">Left button content</scroll-button>
 */
class ScrollButton extends HTMLElement {
  constructor() {
    super();
    this._handleClick = this._handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick);
  }

  static get observedAttributes() {
    return ['direction', 'target', 'amount', 'behavior'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    this[name] = newValue;
  }

  _handleClick(event) {
    event.preventDefault();
    
    // Get the target element to scroll
    const targetId = this.getAttribute('target');
    if (!targetId) {
      console.warn('ScrollButton: No target attribute specified');
      return;
    }
    
    const targetElement = document.getElementById(targetId);
    if (!targetElement) {
      console.warn(`ScrollButton: Target element with ID "${targetId}" not found`);
      return;
    }
    
    // Get the scroll direction
    const direction = this.getAttribute('direction') || 'right';
    
    // Get the scroll amount (default: 300px)
    const amount = parseInt(this.getAttribute('amount') || '300', 10);
    
    // Get the scroll behavior (default: 'smooth')
    const behavior = this.getAttribute('behavior') || 'smooth';
    
    // Determine the scroll properties based on direction
    let scrollOptions = { behavior };
    
    switch (direction) {
      case 'left':
        scrollOptions.left = targetElement.scrollLeft - amount;
        break;
      case 'right':
        scrollOptions.left = targetElement.scrollLeft + amount;
        break;
      case 'up':
        scrollOptions.top = targetElement.scrollTop - amount;
        break;
      case 'down':
        scrollOptions.top = targetElement.scrollTop + amount;
        break;
      default:
        console.warn(`ScrollButton: Invalid direction "${direction}"`);
        return;
    }
    
    // Perform the scroll
    targetElement.scrollTo(scrollOptions);
  }
}

// Define the new element
customElements.define('scroll-button', ScrollButton);

export default ScrollButton;
